import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/ui/Header';
import QuickActionCard from './components/QuickActionCard';
import { useLocations } from '@/context/LocationsContext';
import useTranslation from '@/hooks/useTranslation';
import useNotifications from '@/hooks/useNotifications';
import { NotificationSection } from 'shared';

const EmployeeSelfService = () => {
  const { t } = useTranslation(['header', 'common']);
  const {
    locations,
    loading: locationsLoading,
    error: locationsError,
    refetch: refetchLocations,
  } = useLocations();
  const navigate = useNavigate();
  const { notifications: userNotifications } = useNotifications(10000);

  const [selectedLocation, setSelectedLocation] = useState(null);

  useEffect(() => {
    if (!Array.isArray(locations) || locations.length === 0) {
      return;
    }

    setSelectedLocation((prev) => {
      if (prev && locations.some((locationItem) => locationItem?.id === prev?.id)) {
        return prev;
      }
      return locations[0];
    });
  }, [locations]);

  const unseenCountsBySection = useMemo(() => {
    return (userNotifications ?? []).reduce((acc, notification) => {
      if (!notification?.seen && notification?.section) {
        acc[notification.section] = (acc[notification.section] || 0) + 1;
      }
      return acc;
    }, {});
  }, [userNotifications]);

  const getBadgeForSection = useCallback(
    (section) => {
      const count = unseenCountsBySection?.[section];
      if (typeof count !== 'number' || count <= 0) {
        return null;
      }
      return count;
    },
    [unseenCountsBySection],
  );

  const notifications = useMemo(
    () => ({
      messages: getBadgeForSection(NotificationSection.INTERNAL_CHAT),
      timeOff: getBadgeForSection(NotificationSection.ABSENCE_MANAGEMENT),
    }),
    [getBadgeForSection],
  );

  const quickActions = useMemo(
    () => [
      {
        title: 'Absences',
        description: 'Request and review time off.',
        iconName: 'Clock',
        variant: 'default',
        badge: getBadgeForSection(NotificationSection.ABSENCE_MANAGEMENT),
        onClick: () => navigate('/time-off-management')
      },
      {
        title: 'Chat',
        description: 'Connect with your managers and teammates.',
        iconName: 'MessageSquare',
        variant: 'default',
        badge: getBadgeForSection(NotificationSection.INTERNAL_CHAT),
        onClick: () => navigate('/internal-messaging')
      },
      {
        title: 'Trainings',
        description: 'Access learning resources and training materials.',
        iconName: 'LibraryBig',
        variant: 'default',
        badge: getBadgeForSection(NotificationSection.TRAINING_MANAGEMENT),
        onClick: () => navigate('/trainings')
      },
      {
        title: 'Incident Reports',
        description: 'See safety activity and submit new reports.',
        iconName: 'ShieldAlert',
        variant: 'error',
        badge: getBadgeForSection(NotificationSection.INCIDENT_MANAGEMENT),
        onClick: () => navigate('/incident-reports')
      }
    ],
    [getBadgeForSection, navigate],
  );

  const handleLocationChange = (location) => {
    setSelectedLocation(location ?? null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        userRole="employee"
        selectedLocation={
          selectedLocation?.name ?? (locationsLoading ? t('header.location.loadingLocations') : '')
        }
        locations={locations}
        onLocationChange={handleLocationChange}
        notifications={notifications}
      />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {locationsError && (
            <div className="mb-4 bg-error/10 border border-error/20 rounded-lg px-4 py-3 text-sm text-error">
              Unable to load locations. <button type="button" className="underline" onClick={() => refetchLocations().catch(() => {})}>Try again</button>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {quickActions?.map((action, index) => (
              <QuickActionCard
                key={index}
                title={action?.title}
                description={action?.description}
                iconName={action?.iconName}
                variant={action?.variant}
                badge={action?.badge}
                onClick={action?.onClick}
              />
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default EmployeeSelfService;
